import Swal from 'sweetalert2';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { setSmsPhone } from '../action_parent';

import { mapCartTotalsResponseToCamelCase } from '../../utils/api';

export const V3_POP_ITEM_FROM_CONFIG = 'V3_POP_ITEM_FROM_CONFIG';
export const V3_SET_PRODUCT_CONFIG = 'V3_SET_PRODUCT_CONFIG';
export const V3_CLEAR_PRODUCT_CONFIG = 'V3_CLEAR_PRODUCT_CONFIG';
export const V3_ADD_TO_CART = 'V3_ADD_TO_CART';
export const V3_MARK_RETOUCHING_PROMPTED = 'V3_MARK_RETOUCHING_PROMPTED';
export const V3_DECLINE_INTERSTITIAL_PRODUCT =
  'V3_DECLINE_INTERSTITIAL_PRODUCT';
export const V3_DECLINE_PRE_CART_OFFER_PRODUCT =
  'V3_DECLINE_PRE_CART_OFFER_PRODUCT';
export const V3_DECLINE_RETOUCHING = 'V3_DECLINE_RETOUCHING';
export const V3_DECLINE_PERSONALIZATION = 'V3_DECLINE_PERSONALIZATION';
export const V3_SUBMIT_PERSONALIZATION = 'V3_SUBMIT_PERSONALIZATION';
export const V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS =
  'V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS';
export const V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS =
  'V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS';
export const V3_STORE_CART_SUCCESS = 'V3_STORE_CART_SUCCESS';
export const V3_STORE_CART_ERROR = 'V3_STORE_CART_ERROR';
export const V3_STORE_CART = 'V3_STORE_CART';
export const V3_SUBMITTING_CART = 'V3_SUBMITTING_CART';
export const V3_SUBMIT_CART_SUCCESS = 'SUCCESS';
export const V3_SUBMIT_CART_ERROR = 'V3_SUBMIT_CART_ERROR';
export const V3_CLEAR_CART = 'V3_CLEAR_CART';
export const V3_REMOVE_FROM_CONFIG_STACK = 'V3_REMOVE_FROM_CONFIG_STACK';
export const V3_ADD_TO_CONFIG_STACK = 'V3_ADD_TO_CONFIG_STACK';
export const V3_REMOVE_INDEX_FROM_PRODUCT_CONFIG =
  'V3_REMOVE_INDEX_FROM_PRODUCT_CONFIG';
export const V3_REMOVE_CART_ITEM = 'V3_REMOVE_CART_ITEM';
export const V3_RESET_RETOUCHING = 'V3_RESET_RETOUCHING';
export const V3_UPDATE_PARENT_SMS_NUMBER = 'V3_UPDATE_PARENT_SMS_NUMBER';
export const V3_UPDATE_SMS_NUMBER_CONFIRMED = 'V3_UPDATE_SMS_NUMBER_CONFIRMED';
export const V3_REMOVE_ITEM_FROM_CONFIG = 'V3_REMOVE_ITEM_FROM_CONFIG';
export const V3_APPLY_DISCOUNT_CODE_TO_CART = 'V3_APPLY_DISCOUNT_CODE_TO_CART';
export const V3_CHECKOUT_LOADING = 'V3_CHECKOUT_LOADING';
export const V3_RESET_SUBMITTING_ORDER = 'V3_RESET_SUBMITTING_ORDER';
export const V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED =
  'V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED';
export const V3_CONFIRM_SHIPPING_ADDRESS = 'V3_CONFIRM_SHIPPING_ADDRESS';
export const V3_SET_ACCEPTED_PICS_PLUS = 'V3_SET_ACCEPTED_PICS_PLUS';
export const V3_SET_CART_UUID = 'V3_SET_CART_UUID';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const missingRequiredProductOptionsMessage = (missingOption = {}) => {
  const productName = escapeHtml(missingOption.product_name || 'This product');
  const optionSetName = escapeHtml(
    missingOption.option_set_name || 'a required option',
  );
  const reason = missingOption.reason;

  if (
    reason === 'invalid_product_option_selection' ||
    reason === 'invalid_selection'
  ) {
    return `${productName} has an invalid option selection for ${optionSetName}.`;
  }

  return `${productName} requires an option selection for ${optionSetName}.`;
};

const missingRequiredProductOptionsHtml = (missingOptions = []) => {
  const missingOptionsList = missingOptions
    .map(
      (missingOption) =>
        `<li>${missingRequiredProductOptionsMessage(missingOption)}</li>`,
    )
    .join('');

  const details = missingOptionsList
    ? `<ul>${missingOptionsList}</ul>`
    : '<p>One or more products in your cart require product option updates.</p>';

  return `
    <p>Some product options in your cart need attention before checkout can continue.</p>
    ${details}
    <p>Please return to your cart or product flow and update or re-add the affected item before submitting.</p>
  `;
};

export const setProductConfig =
  ({ index, itemToAdd }) =>
  (dispatch, getState) => {
    const {
      isPrepay,
      postpayRequiresSameValuePersonalization,
      prepayRequiresSameValuePersonalization,
    } = getState().shoot;

    dispatch({
      type: V3_SET_PRODUCT_CONFIG,
      payload: {
        index,
        isPrepay,
        itemToAdd,
        prepayRequiresSameValuePersonalization,
      },
    });
  };

export function popItemFromConfig(index, section) {
  return {
    type: V3_POP_ITEM_FROM_CONFIG,
    payload: { index, section },
  };
}

export function removeItemFromConfig(index, history) {
  return {
    type: V3_REMOVE_ITEM_FROM_CONFIG,
    payload: { index, history },
  };
}

export function clearProductConfig(key) {
  return {
    type: V3_CLEAR_PRODUCT_CONFIG,
    payload: key,
  };
}

export function removeIndexFromProductConfig({ toRemove, index, history }) {
  return {
    type: V3_REMOVE_INDEX_FROM_PRODUCT_CONFIG,
    payload: { toRemove, index, history },
  };
}

export function removeFromConfigStack(number) {
  return {
    type: V3_REMOVE_FROM_CONFIG_STACK,
    payload: number,
  };
}

export function addToConfigStack(paths) {
  return {
    type: V3_ADD_TO_CONFIG_STACK,
    payload: paths,
  };
}

export function declineRetouching(orderKey) {
  return {
    type: V3_DECLINE_RETOUCHING,
    payload: { orderKey },
  };
}

export function declinePersonalization(orderKey) {
  return {
    type: 'V3_DECLINE_PERSONALIZATION',
    payload: { orderKey },
  };
}

export function submitPersonalization(orderKey, value) {
  return {
    type: 'V3_SUBMIT_PERSONALIZATION',
    payload: { orderKey, value },
  };
}

export function setPersonalizationOnAllEligibleCartItems(
  orderKey,
  personalizationValue,
  redirectSource = null,
) {
  return {
    type: V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS,
    payload: { personalizationValue, redirectSource, orderKey },
  };
}

export function skipPersonalizationOnAllCartItems(
  orderKey,
  redirectSource = null,
) {
  return {
    type: V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS,
    payload: { redirectSource, orderKey },
  };
}

export function markRetouchingPrompted(orderKey) {
  return {
    type: V3_MARK_RETOUCHING_PROMPTED,
    payload: { orderKey },
  };
}

export const storeCart = () => async (dispatch, getState) => {
  const { discountCodes, orders, uuid } = getState().v3Order;
  const { studentsDetails } = getState().bnlPreShoot;
  const { items: voucherItems, voucherSubtotalCents } =
    getState().cartVoucherItems || {};

  dispatch({ type: V3_STORE_CART });

  // Retrieve the cookie value and attempt to parse it as an integer
  const rawValue = Cookies.get('campaign_event_id');
  const parsedValue = parseInt(rawValue, 10);

  const campaignEventId = isNaN(parsedValue) ? null : parsedValue;

  try {
    const response = await axios.post('/api/v3/orders/store-cart', {
      campaignEventId,
      discountCodes,
      orders,
      studentsDetails,
      uuid,
      voucherSubtotalCents: voucherSubtotalCents || 0,
      voucherItems: voucherItems || [],
    });

    if (
      response?.response?.status === 422 &&
      response?.response?.data?.message === 'cart already submitted'
    ) {
      const uuid = response?.response?.data?.uuid;

      Sentry.captureMessage(
        `Cart with UUID ${uuid} was previously submitted and paid for`,
      );
      dispatch({ type: V3_CLEAR_CART });

      return Swal.fire({
        icon: 'warning',
        html: `
          <p>There was an issue detected with your order(s) &mdash; it appears you've previously submitted and paid for your current cart.<p>
          <p>We've cleared your cart contents, please try again.</p>
          <p>When submitting any future payments, please ensure you're redirected to the 'success' screen before closing your browser.<p>
        `,
      }).then((result) => {
        window.location.href = '/v3/offers';
      });
    }

    const cartTotals = mapCartTotalsResponseToCamelCase(response.data);
    const offerPicsPlus = response.data.offer_pics_plus === true ? true : false;
    const isPicsPlusMember =
      response.data.is_pics_plus_member === true ? true : false;
    const picsPlusSubscriptionInfo =
      response.data.pics_plus_subscription_info || null;
    const subscriberCount = response.data.subscriber_count || 0;
    const picsPlusExperiment = response.data.pics_plus_experiment || null;

    dispatch({
      type: V3_STORE_CART_SUCCESS,
      payload: {
        cartTotals,
        offerPicsPlus,
        isPicsPlusMember,
        picsPlusSubscriptionInfo,
        subscriberCount,
        picsPlusExperiment,
      },
    });
  } catch (error) {
    console.log(error);

    dispatch({ type: V3_STORE_CART_ERROR });

    Swal.fire({
      icon: 'error',
      html: '<p>There was an error loading your cart. Please try again. If the error persists, you may need to restart your order.</p>',
    }).then(() => {
      dispatch({ type: V3_CLEAR_CART });
      window.location.href = '/dashboard';
    });
  }
};

// TODO: clean this up
export const submitCart =
  (stripeCancelUrl, addedPicsPlus = false) =>
  async (dispatch, getState) => {
    const { discountCodes, orders, shippingAddressConfirmed, uuid } =
      getState().v3Order;
    const { items: voucherItems, voucherSubtotalCents } =
      getState().cartVoucherItems || {};

    // Debug logging for ClassAct orders
    console.log('🔍 SUBMITTING CART - Orders:', orders);
    Object.keys(orders).forEach((orderKey) => {
      const order = orders[orderKey];
      console.log(`  Order ${orderKey}:`);
      console.log('    additionalData:', order.additionalData);
      console.log('    isPrepay:', order.isPrepay);
      console.log('    event:', order.event);
    });

    dispatch({ type: V3_SUBMITTING_CART });

    try {
      const response = await axios.post('/api/v3/orders/submit', {
        addedPicsPlus,
        discountCodes,
        orders,
        shippingAddressConfirmed,
        stripe_cancel_url: stripeCancelUrl,
        uuid,
        voucherSubtotalCents: voucherSubtotalCents || 0,
        voucherItems: voucherItems || [],
      });

      const {
        payment_required,
        message,
        no_payment_required_redirect_url,
        session_url,
      } = response.data;

      if (
        !payment_required &&
        message === 'no payment required' &&
        no_payment_required_redirect_url
      ) {
        return (window.location.href = no_payment_required_redirect_url);
      }

      if (session_url) {
        return (window.location.href = session_url);
      } else {
        console.error('session_url is not provided');
        dispatch({
          type: V3_SUBMIT_CART_ERROR,
          payload: 'session_url is missing in the response.',
        });
      }
    } catch (error) {
      console.log('ERROR STATE REACHED');
      console.log(error);

      const response = error?.response;

      // Handle different 422 error scenarios
      if (response?.status === 422) {
        const { data } = response;

        if (data?.orders_where_prepay_closed?.length > 0) {
          const eventName = data.orders_where_prepay_closed[0]?.event_name;
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission &mdash; prepay orders are closed for <span style="font-style: italic;">${eventName}</span></p>
              <p>Please remove items for this event from your cart and try again.</p>
              <p>You can place an order for <span style="font-style: italic;">${eventName}</span> as soon as your student's image is ready.</p>
            `,
          });
        }

        if (data?.error_identifier === 'phase_mismatch') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });
          const firstMismatch = data.phase_mismatch_orders[0];
          return Swal.fire({
            icon: 'warning',
            title: 'Offers Have Changed',
            html: `
              <p>The offers for <span style="font-style: italic;">${escapeHtml(
                firstMismatch.event_name,
              )}</span> have changed since you added items to your cart.</p>
              <p>Please remove items for this event from your cart and add them again to see the current pricing and available options.</p>
              <p>We apologize for any inconvenience this may cause.</p>
            `,
          });
        }

        if (data?.error_identifier === 'cart_already_has_order') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: you've previously placed an order for your current cart.</p>
              <p>This situation can occur if your cart wasn't fully cleared after a previous order. Please clear your cart completely and retry your order.</p>
            `,
          });
        }

        if (data?.error_identifier === 'missing_incentives') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: some of the incentive products you qualified for are missing from your cart.</p>
              <p>Please clear your cart completely and retry your order. If you run into this issue again, please contact customer support.</p>
            `,
          });
        }

        if (data?.error_identifier === 'orders_with_multiple_price_lists') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: one of your student's orders contains items from multiple events.</p>
              <p>Please clear your cart and retry your order. Please only place orders for one event per student. If you run into this issue again, please contact customer support.</p>
            `,
          });
        }

        if (data?.error_identifier === 'missing_personalization') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>One or more products in your cart require personalization.</p>
              <p>Please go back and enter a personalization value for all required products before submitting.</p>
            `,
          });
        }

        if (data?.error_identifier === 'missing_required_product_options') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            title: 'Product Options Need Attention',
            html: missingRequiredProductOptionsHtml(
              data?.missing_required_product_options,
            ),
          });
        }
      }

      // General error handling
      dispatch({
        type: V3_SUBMIT_CART_ERROR,
        payload: error.message || 'An unexpected error occurred.',
      });
    }
  };

export const submitCartSuccess = () => ({
  type: V3_SUBMIT_CART_SUCCESS,
});

export const storeSuccessAndBuildOrder =
  (sessionId, uuid) => async (dispatch, getState) => {
    try {
      const result = await axios.post(
        '/api/v3/orders/store-success-build-order',
        {
          sessionId,
          uuid,
        },
      );

      if (result?.status === 200) {
        // Clear the cart
        dispatch(clearCart());

        // Clear the campaign event ID cookie
        Cookies.remove('campaign_event_id');
      } else {
        // TODO: send error to Sentry
      }
    } catch (error) {
      // TODO: send error to Sentry
    }
  };

export const clearCart = () => (dispatch, getState) => {
  const { uuid } = getState().v3Order;

  // Clean up Pics+ localStorage entries for this cart
  if (uuid) {
    localStorage.removeItem(`picsPlusModalShown_${uuid}`);
    localStorage.removeItem(`picsPlusAccepted_${uuid}`);
  }

  dispatch({ type: V3_CLEAR_CART });
};

export const removeCartItem = (cartItemId, orderKey) => {
  return {
    type: V3_REMOVE_CART_ITEM,
    payload: { cartItemId, orderKey },
  };
};

export const removeCartItems = (cartItemIds, orderKey) => (dispatch) => {
  cartItemIds.forEach((cartItemId) => {
    dispatch(removeCartItem(cartItemId, orderKey));
  });
};

export const resetRetouching = (orderKey) => ({
  type: V3_RESET_RETOUCHING,
  payload: { orderKey },
});

export const updateSMSNumberConfirmed = (value) => ({
  type: 'V3_UPDATE_SMS_NUMBER_CONFIRMED',
  payload: value,
});

export const updateParentSMSNumber = (id, phoneNumber) => {
  const data = { sms_phone: phoneNumber };

  return async (dispatch) => {
    try {
      const response = await axios.patch(`/api/v2/parents/${id}`, data);
      if (response.status >= 200 && response.status < 300) {
        dispatch({ type: 'V3_UPDATE_SMS_NUMBER_CONFIRMED', payload: true });
        dispatch(setSmsPhone(phoneNumber));
      } else {
        throw new Error('Something went wrong'); // throw an error if the response is not 2xx
      }
    } catch (error) {
      Swal.fire('Something went wrong', error.toString(), 'error'); // show the error to the user
    }
  };
};

export const declineInterstitialProduct = (orderKey, productId) => ({
  type: V3_DECLINE_INTERSTITIAL_PRODUCT,
  payload: { orderKey, productId },
});

export const declinePreCartOfferProduct = (
  orderKey,
  productId,
  redirectSource = null,
) => ({
  type: V3_DECLINE_PRE_CART_OFFER_PRODUCT,
  payload: { productId, redirectSource, orderKey },
});

export const checkAndApplyDisountCode =
  (discountCode) => async (dispatch, getState) => {
    dispatch(checkoutIsLoading(true));

    const { pricing_model } = getState()?.shoot?.shoot || {};
    const dataToSubmit = {
      code: discountCode,
      company_pricing_model_id: pricing_model?.id,
    };

    if (!dataToSubmit.code) {
      dispatch(checkoutIsLoading(false));
      return Swal.fire('Error', 'You must submit a code', 'error');
    }

    try {
      const response = await axios.get(
        `/api/v2/discount-codes/apply-discount-code`,
        {
          params: dataToSubmit,
        },
      );

      const { data } = response;

      if (data) {
        if (!data.valid) {
          return Swal.fire('Something went wrong', data.message, 'warning');
        }

        dispatch(applyDiscountCodeToCart(data));
        dispatch(storeCart());
      } else {
        return Swal.fire(
          'Something went wrong',
          'Discount Code not found',
          'error',
        );
      }
    } catch (error) {
      return Swal.fire('Something went wrong', error.toString(), 'error');
    } finally {
      dispatch(checkoutIsLoading(false));
    }
  };

export const checkoutIsLoading = (checkoutIsLoading) => ({
  type: V3_CHECKOUT_LOADING,
  payload: { checkoutIsLoading },
});

export const applyDiscountCodeToCart = (data) => ({
  type: V3_APPLY_DISCOUNT_CODE_TO_CART,
  payload: { data },
});

export const resetSubmittingOrder = () => {
  return {
    type: V3_RESET_SUBMITTING_ORDER,
  };
};

export const confirmShippingAddress = () => {
  return {
    type: V3_CONFIRM_SHIPPING_ADDRESS,
  };
};

export const setAcceptedPicsPlus = (accepted) => {
  return {
    type: V3_SET_ACCEPTED_PICS_PLUS,
    payload: { accepted },
  };
};

export const logPicsPlusInteraction =
  (eventType, triggeredFrom = null, metadata = {}) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const cartUuid = state.v3Order.uuid;
      const picsPlusExperiment = state.v3Order.picsPlusExperiment;

      if (!cartUuid) {
        console.warn('Cannot log Pics+ interaction: cart UUID not available');
        return;
      }

      // Include experiment context in metadata
      const enrichedMetadata = {
        ...metadata,
        experiment_id: picsPlusExperiment?.experiment_id || null,
        experiment_name: picsPlusExperiment?.experiment_name || null,
        variant_id: picsPlusExperiment?.variant_id || null,
        variant_name: picsPlusExperiment?.variant_name || null,
        price_cents: picsPlusExperiment?.price_cents || null,
      };

      const payload = {
        cart_uuid: cartUuid,
        event_type: eventType,
        triggered_from: triggeredFrom,
        metadata: enrichedMetadata,
      };

      await axios.post('/api/v3/pics-plus-interactions', payload);
    } catch (error) {
      console.error('Error logging Pics+ interaction:', error);
    }
  };
